import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DoneIcon from '@mui/icons-material/Done';
import { useContext } from 'react';
import ComplexFilterGroup from './ComplexFilterGroup';
import { ComplexFilterContext } from './ComplexFilterContext';

/**
 * Combined filter UI. Card-based layout:
 *
 *   ┌─ AND group 1 ──────────────────────────────┐
 *   │ [row] [row]                + Add filter    │
 *   └────────────────────────────────────────────┘
 *                    OR
 *   ┌─ AND group 2 ──────────────────────────────┐
 *   │ [row]                      + Add filter    │
 *   └────────────────────────────────────────────┘
 *
 *   + Add filter group                  [ Apply filters ]
 *
 * Logical semantics: filters inside a group are AND'ed; groups are OR'd. The
 * chip between cards makes that explicit instead of relying on operator
 * precedence the user has to memorise.
 */
const ComplexFilter = ({
  internalFilterParams,
  setFilterParams,
  setInitialFilterGroups,
  setPage,
  setBodyParams,
}) => {
  const { filterGroups, createFilterGroup, setFilterGroup, createFilter } =
    useContext(ComplexFilterContext);

  const groupEntries = Object.entries(filterGroups);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={0}>
        {groupEntries.map(([groupId, group], idx) => (
          <Box key={groupId}>
            {idx > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                <Chip size="small" label="OR" color="primary" variant="filled" />
              </Box>
            )}
            <ComplexFilterGroup group={group} />
          </Box>
        ))}

        {groupEntries.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No filters yet. Add a filter group to start narrowing the list.
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
      >
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={() => {
            const group = createFilterGroup();
            const filter = createFilter();
            group.filters[filter.filterId] = filter;
            setFilterGroup(group.groupId, group);
          }}
        >
          Add filter group
        </Button>
        <Button
          variant="contained"
          startIcon={<DoneIcon />}
          onClick={() => {
            setInitialFilterGroups(filterGroups);
            setPage(0);
            setFilterParams(internalFilterParams);
            setBodyParams('');
          }}
        >
          Apply filters
        </Button>
      </Stack>
    </Box>
  );
};

export default ComplexFilter;
